import { useMemo, useState } from "react";
import { sttPlace, sttShort, sttTitle } from "./stt";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { clinicSessionsApi } from "../../shared/api/clinicSessions";
import { apiError } from "../../shared/api/client";
import { formatTime, isoDate, startOfWeek } from "../../shared/format";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import type { AppointmentSummary } from "../../shared/types/appointment";
import { PageHeader } from "../../shared/ui/PageHeader";
import { ErrorState } from "../../shared/ui/ErrorState";
import { SkeletonRows } from "../../shared/ui/Skeleton";
import { useKeyboardShortcut } from "../../shared/ui/useKeyboardShortcut";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Khung giờ luôn hiện (7:00 → 17:00); mở rộng theo buổi khám và theo lịch thật — xem {@link gridHours}. */
const BASE_HOURS = { from: 7, to: 17 };
const WEEKDAYS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const DND_MIME = "application/x-addy-appt";

/** Reschedule statuses that the BE accepts (LIVE). Drag-disabled on terminal ones. */
const RESCHEDULABLE = new Set(["REQUESTED", "CONFIRMED", "CHECKED_IN"]);

/** Per-status colour treatment for the small appointment blocks in the grid. */
const APPT_COLORS: Record<string, string> = {
  REQUESTED: "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
  CONFIRMED: "border-primary/30 bg-primary/10 text-primary hover:bg-primary/20",
  CHECKED_IN: "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100",
  COMPLETED: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  NO_SHOW: "border-border bg-muted text-muted-foreground hover:bg-muted/80",
  CANCELLED:
    "border-border bg-muted text-muted-foreground line-through hover:bg-muted/80",
};

export function AppointmentCalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Tuần + bác sĩ đang xem nằm trên URL (`?week=<thứ Hai>`); một ngày bất kỳ trên URL được neo
  // về thứ Hai của tuần đó. Mặc định tuần này.
  const { filters, set } = useUrlFilters({
    week: param.isoDate(() => isoDate(startOfWeek(new Date()))),
    doctorId: param.string(),
  });
  const { doctorId } = filters;
  const weekStart = useMemo(
    () => startOfWeek(new Date(`${filters.week}T00:00:00`)),
    [filters.week]
  );
  const setWeekStart = (d: Date) => set({ week: isoDate(startOfWeek(d)) });
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "error" | "ok"; text: string } | null>(null);

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const week = useQuery({
    queryKey: appointmentQueryKeys.forWeek(isoDate(weekStart)),
    queryFn: () => appointmentsApi.forWeek(weekStart),
  });

  // Buổi khám đang có hiệu lực (#19861): buổi tối 18–20h phải có hàng trên lịch. Không đọc được
  // (thiếu quyền clinic.read) thì vẫn còn khung cơ bản + giờ của chính các lịch trong tuần.
  const sessions = useQuery({
    queryKey: ["clinic-sessions", "effective"],
    queryFn: () => clinicSessionsApi.effective(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const dayHeaders = useMemo(() => {
    return WEEKDAYS.map((wd, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return {
        label: wd,
        date: d,
        isToday: isoDate(d) === isoDate(today),
        isoDate: isoDate(d),
      };
    });
  }, [weekStart, today]);

  /** Map (isoDate, hour) → appointments[]. Keyed by the appointment's actual date
   *  (not weekday) so an out-of-week appointment can never land in the wrong week's
   *  matching-weekday column. */
  const grid = useMemo(() => {
    const map = new Map<string, AppointmentSummary[]>();
    for (const a of week.data ?? []) {
      if (doctorId && a.doctorId !== doctorId) continue;
      const d = new Date(a.startAt);
      const key = `${isoDate(d)}|${d.getHours()}`;
      const bucket = map.get(key) ?? [];
      bucket.push(a);
      map.set(key, bucket);
    }
    return map;
  }, [week.data, doctorId]);

  const dayIsoDates = useMemo(() => dayHeaders.map((h) => h.isoDate), [dayHeaders]);

  const hours = useMemo(
    () =>
      gridHours(
        (sessions.data?.sessions ?? []).filter((s) => s.active),
        (week.data ?? []).map((a) => new Date(a.startAt).getHours())
      ),
    [sessions.data, week.data]
  );

  const shiftWeek = (offset: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + offset * 7);
    setWeekStart(next);
  };

  // ---- Keyboard shortcuts (M27) ------------------------------------
  // Single-key by design; hook ignores keystrokes inside text inputs so
  // the doctor-filter <select> still receives "n" / arrow keys normally.
  useKeyboardShortcut({ key: "arrowleft" }, () => shiftWeek(-1), [weekStart]);
  useKeyboardShortcut({ key: "arrowright" }, () => shiftWeek(1), [weekStart]);
  useKeyboardShortcut({ key: "t" }, () => setWeekStart(startOfWeek(new Date())), []);
  useKeyboardShortcut({ key: "n" }, () => navigate("/appointments/new"), [navigate]);
  useKeyboardShortcut({ key: "d" }, () => navigate("/appointments/day"), [navigate]);
  useKeyboardShortcut(
    { key: "w" },
    () => {
      /* already on week view; no-op to satisfy the cheatsheet contract */
    },
    []
  );

  /**
   * Drop handler — compute the new startAt from the target (day, hour) cell and
   * fire the reschedule mutation. Optimistic UI: the appointment visually moves
   * to the new cell while the request is in flight; on a 409 conflict we revert
   * by refetching the week and surface a toast.
   */
  const reschedule = useMutation({
    mutationFn: (vars: { id: string; startAt: string; durationMinutes: number }) =>
      appointmentsApi.reschedule(vars.id, {
        startAt: vars.startAt,
        durationMinutes: vars.durationMinutes,
        reason: "Kéo thả từ lịch tuần",
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: appointmentQueryKeys.forWeek(isoDate(weekStart)) });
      const prev = qc.getQueryData<AppointmentSummary[]>(
        appointmentQueryKeys.forWeek(isoDate(weekStart))
      );
      if (prev) {
        qc.setQueryData<AppointmentSummary[]>(
          appointmentQueryKeys.forWeek(isoDate(weekStart)),
          prev.map((a) => (a.id === vars.id ? { ...a, startAt: vars.startAt } : a))
        );
      }
      return { prev };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(
          appointmentQueryKeys.forWeek(isoDate(weekStart)),
          ctx.prev
        );
      }
      const detail = apiError(e).detail;
      const isConflict = (detail ?? "").toLowerCase().includes("khung giờ");
      setToast({
        kind: "error",
        text: isConflict ? detail : `Không đổi được giờ: ${detail}`,
      });
      window.setTimeout(() => setToast(null), 5000);
    },
    onSuccess: () => {
      setToast({ kind: "ok", text: "Đã đổi giờ lịch hẹn." });
      window.setTimeout(() => setToast(null), 2500);
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.forWeek(isoDate(weekStart)) }),
  });

  function handleDrop(targetDayIndex: number, targetHour: number, e: React.DragEvent) {
    e.preventDefault();
    setDragOverKey(null);
    const payload = e.dataTransfer.getData(DND_MIME);
    if (!payload) return;
    let parsed: { id: string; durationMinutes: number; originalStartAt: string };
    try {
      parsed = JSON.parse(payload);
    } catch {
      return;
    }
    // Compose new start from (weekStart + targetDayIndex days, targetHour:00).
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + targetDayIndex);
    newStart.setHours(targetHour, 0, 0, 0);
    // Skip no-op drops (same hour, same day).
    if (new Date(parsed.originalStartAt).getTime() === newStart.getTime()) return;
    reschedule.mutate({
      id: parsed.id,
      startAt: newStart.toISOString(),
      durationMinutes: parsed.durationMinutes,
    });
  }

  return (
    <div>
      <PageHeader
        title="Lịch hẹn"
        description="Lịch tuần theo giờ. Click ô để mở chi tiết. Kéo-thả khối lịch sang ô giờ khác để đổi giờ nhanh."
        breadcrumbs={[{ label: "Trang chủ", to: "/dashboard" }, { label: "Lịch hẹn" }]}
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/appointments/day">Lịch ngày</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/appointments/list">Danh sách</Link>
            </Button>
            <Button asChild>
              <Link to="/appointments/new">+ Đặt lịch</Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftWeek(-1)}
          aria-label="Tuần trước"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[220px] text-center text-sm font-semibold text-foreground">
          {dayHeaders[0]!.date.toLocaleDateString("vi-VN")} –{" "}
          {dayHeaders[6]!.date.toLocaleDateString("vi-VN")}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftWeek(1)}
          aria-label="Tuần sau"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setWeekStart(startOfWeek(new Date()))}
        >
          Tuần này
        </Button>
        <Select
          value={doctorId || undefined}
          onValueChange={(v) => set({ doctorId: v === "__all__" ? "" : v })}
        >
          <SelectTrigger className="w-[200px]" aria-label="Lọc bác sĩ">
            <SelectValue placeholder="Mọi bác sĩ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Mọi bác sĩ</SelectItem>
            {doctors.data?.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {toast && (
        <div
          role="status"
          className={cn(
            "my-2 rounded-md border px-3 py-2 text-sm",
            toast.kind === "error"
              ? "border-destructive/30 bg-destructive/10 text-destructive"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          )}
        >
          {toast.text}
        </div>
      )}

      {week.isError && <ErrorState error={week.error} inline />}
      {week.isLoading && <SkeletonRows rows={8} cols={[80, "1fr", "1fr", "1fr", "1fr", "1fr", "1fr", "1fr"]} />}
      {!week.isLoading && !week.isError && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                {/* Header row: time gutter + 7 day columns */}
                <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b bg-muted/40">
                  <div className="px-2 py-2 text-xs font-medium text-muted-foreground">Giờ</div>
                  {dayHeaders.map((h) => (
                    <div
                      key={h.label}
                      className={cn(
                        "flex flex-col items-center border-l px-2 py-2 text-xs font-medium",
                        h.isToday ? "bg-primary/5 text-primary" : "text-muted-foreground"
                      )}
                    >
                      <span>{h.label}</span>
                      <strong className="text-sm text-foreground">{h.date.getDate()}</strong>
                    </div>
                  ))}
                </div>
                {/* Hour rows */}
                <div>
                  {hours.map((hour) => (
                    <DayHourRow
                      key={hour}
                      hour={hour}
                      grid={grid}
                      dayIsoDates={dayIsoDates}
                      dragOverKey={dragOverKey}
                      onOpen={(a) => navigate(`/appointments/${a.id}`)}
                      onDragEnterCell={(key) => setDragOverKey(key)}
                      onDragLeaveCell={() => setDragOverKey(null)}
                      onDropCell={handleDrop}
                    />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DayHourRow({
  hour,
  grid,
  dayIsoDates,
  dragOverKey,
  onOpen,
  onDragEnterCell,
  onDragLeaveCell,
  onDropCell,
}: {
  hour: number;
  grid: Map<string, AppointmentSummary[]>;
  dayIsoDates: string[];
  dragOverKey: string | null;
  onOpen: (a: AppointmentSummary) => void;
  onDragEnterCell: (key: string) => void;
  onDragLeaveCell: () => void;
  onDropCell: (dayIndex: number, hour: number, e: React.DragEvent) => void;
}) {
  return (
    <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b last:border-b-0">
      <div className="px-2 py-2 text-xs text-muted-foreground">
        {String(hour).padStart(2, "0")}:00
      </div>
      {Array.from({ length: 7 }, (_, day) => {
        const cellKey = `${day}|${hour}`;
        const lookupKey = `${dayIsoDates[day]}|${hour}`;
        const isOver = dragOverKey === cellKey;
        return (
          <div
            key={day}
            className={cn(
              "min-h-[44px] space-y-1 border-l p-1 transition-colors",
              isOver && "bg-primary/10 ring-1 ring-inset ring-primary/40"
            )}
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes(DND_MIME)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dragOverKey !== cellKey) onDragEnterCell(cellKey);
              }
            }}
            onDragLeave={() => onDragLeaveCell()}
            onDrop={(e) => onDropCell(day, hour, e)}
          >
            {(grid.get(lookupKey) ?? []).map((a) => {
              const draggable = RESCHEDULABLE.has(a.status);
              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  draggable={draggable}
                  className={cn(
                    "flex min-w-0 cursor-pointer items-center gap-1 rounded-md border px-1.5 py-1 text-xs font-medium",
                    APPT_COLORS[a.status] ?? "border-border bg-muted text-muted-foreground",
                    draggable && "cursor-grab active:cursor-grabbing"
                  )}
                  onClick={() => onOpen(a)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onOpen(a);
                  }}
                  onDragStart={(e) => {
                    if (!draggable) return;
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData(
                      DND_MIME,
                      JSON.stringify({
                        id: a.id,
                        durationMinutes: a.durationMinutes ?? 30,
                        originalStartAt: a.startAt,
                      })
                    );
                  }}
                  title={
                    draggable
                      ? `${sttTitle(a)} · ${a.patientName} · ${a.doctorName} · kéo để đổi giờ`
                      : `${sttTitle(a)} · ${a.patientName} · ${a.doctorName}`
                  }
                >
                  {/* #19552 — khối nhỏ: "STT {số} · {giờ} {tên}" rồi nơi tiếp nhận (Quầy/Phòng) canh
                      phải. STT không co lại; tên mới là phần bị cắt. "—" khi lịch chưa có STT. */}
                  <span
                    className="shrink-0 font-semibold tabular-nums"
                    data-testid="week-block-booking-no"
                  >
                    STT {sttShort(a)}
                  </span>
                  <span className="min-w-0 truncate">
                    · {formatTime(a.startAt)} {a.patientName.split(" ").slice(-1)[0]}
                  </span>
                  <span
                    className={cn(
                      "ml-auto shrink-0 pl-1 text-[11px] font-medium",
                      sttPlace(a) === "Quầy" ? "text-amber-700 dark:text-amber-400" : "opacity-70"
                    )}
                    data-testid="week-block-place"
                  >
                    {sttPlace(a)}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Hàng giờ của lưới lịch tuần (#19861): hợp của khung cơ bản 7–17h, các buổi khám đang hoạt động
 * (buổi tối 18:00–20:30 → tới hàng 20h) và giờ của MỌI lịch trong tuần. Phần thứ ba là lưới an
 * toàn: một lịch ngoài mọi khung đã biết vẫn có hàng của nó thay vì biến mất khỏi lịch.
 */
export function gridHours(
  sessions: { startTime: string; endTime: string }[],
  appointmentHours: number[]
): number[] {
  let lo = BASE_HOURS.from;
  let hi = BASE_HOURS.to;
  for (const s of sessions) {
    const start = Number(s.startTime.slice(0, 2));
    const [eh, em] = s.endTime.split(":").map(Number);
    // 20:30 kết thúc trong hàng 20h; 20:00 kết thúc đúng lúc hàng 20h bắt đầu → hàng cuối là 19h.
    const end = em > 0 ? eh : eh - 1;
    if (Number.isFinite(start)) lo = Math.min(lo, start);
    if (Number.isFinite(end)) hi = Math.max(hi, end);
  }
  for (const h of appointmentHours) {
    lo = Math.min(lo, h);
    hi = Math.max(hi, h);
  }
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}
